import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Button, Select, Spin } from "antd";
import { useDebounce } from "use-debounce";
import { useWarehouseList } from "../hooks/use-warehouse-list";

interface WarehouseSelectProps {
  value?: string;
  onChange?: (value: string, option?: any) => void;
  status?: "error" | "warning";
  placeholder?: string;
}

export const WarehouseSelect: React.FC<WarehouseSelectProps> = ({
  value,
  onChange,
  status,
  placeholder = "Select Center",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch] = useDebounce(searchTerm, 300);
  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
  } = useWarehouseList({
    search: debouncedSearch,
    size: 10,
  });

  const options = useMemo(() => {
    if (!data?.pages) return [];

    const allOptions = [];
    for (const page of data.pages) {
      // Try different paths
      const content = page.data?.content || page.content || [];
      for (const item of content) {
        if (item.id && item.centerName) {
          allOptions.push({
            label: item.centerName,
            value: String(item.id),
          });
        }
      }
    }

    return allOptions;
  }, [data?.pages]);

  // Manual load more function
  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // Auto scroll detection
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const scrollTop = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const clientHeight = target.clientHeight;

      // Check if scrolled to bottom
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        if (hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  // Setup scroll listener
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", handleScroll as any);
      return () =>
        scrollElement.removeEventListener("scroll", handleScroll as any);
    }
  }, [handleScroll]);

  const dropdownRender = (menu: React.ReactElement) => (
    <div>
      <div ref={scrollRef} style={{ maxHeight: 300, overflowY: "auto" }}>
        {menu}

        {/* Show loading indicator */}
        {isFetchingNextPage && (
          <div style={{ textAlign: "center", padding: "12px" }}>
            <Spin size="small" />
            <div style={{ fontSize: "12px", marginTop: "4px" }}>
              Loading more...
            </div>
          </div>
        )}

        {/* Show load more button as fallback */}
        {hasNextPage && !isFetchingNextPage && options.length > 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "12px",
              borderTop: "1px solid #f0f0f0",
            }}
          >
            <Button
              type="link"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleLoadMore();
              }}
            >
              Load more ({options.length} /{" "}
              {data?.pages?.[0]?.data?.totalElements ||
                data?.pages?.[0]?.totalElements ||
                "?"}
              )
            </Button>
          </div>
        )}

        {/* Show end of list */}
        {!hasNextPage && options.length > 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "8px",
              color: "#999",
              fontSize: "12px",
            }}
          >
            End of list ({options.length} items)
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Select
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      status={status}
      showSearch
      onSearch={setSearchTerm}
      loading={isLoading || isFetching}
      filterOption={false}
      style={{ width: "100%" }}
      dropdownRender={dropdownRender}
      options={options}
      notFoundContent={isLoading ? "Loading..." : "No centers found"}
      allowClear
    />
  );
};
